import axios from "axios";
import { API_BASE_URL, attachApiErrorHandler } from "./authService";

/**
 * The platform (superadmin) token and an organization token are signed with the
 * same secret but are firewalled from each other: each is rejected on the
 * other's routes. So the panel gets its own axios instance, its own storage
 * keys, and — critically — never sends `x-organization-id`, because there is no
 * organization context on `/superadmin/**`.
 */
export const SUPERADMIN_TOKEN_KEY = "sa_api_token";
export const SUPERADMIN_REFRESH_KEY = "sa_refresh_token";
export const SUPERADMIN_ADMIN_KEY = "sa_admin";

export const superadminClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
});

export const getSuperadminToken = (): string | null =>
  localStorage.getItem(SUPERADMIN_TOKEN_KEY);

export const getSuperadminRefreshToken = (): string | null =>
  localStorage.getItem(SUPERADMIN_REFRESH_KEY);

export const setSuperadminTokens = (
  accessToken: string,
  refreshToken: string
): void => {
  localStorage.setItem(SUPERADMIN_TOKEN_KEY, accessToken);
  localStorage.setItem(SUPERADMIN_REFRESH_KEY, refreshToken);
};

export const clearSuperadminSession = (): void => {
  localStorage.removeItem(SUPERADMIN_TOKEN_KEY);
  localStorage.removeItem(SUPERADMIN_REFRESH_KEY);
  localStorage.removeItem(SUPERADMIN_ADMIN_KEY);
};

export const isSuperadminAuthenticated = (): boolean => !!getSuperadminToken();

superadminClient.interceptors.request.use(
  (config: any) => {
    const token = getSuperadminToken();
    config.headers = config.headers || {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Never attach x-organization-id here — the panel has no org context.
    delete config.headers["x-organization-id"];
    return config;
  },
  (error) => Promise.reject(error)
);

let isRedirectingToLogin = false;

const redirectToLogin = (): void => {
  clearSuperadminSession();
  if (typeof window === "undefined") return;
  if (window.location?.pathname === "/login") return;
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  window.location.replace("/login");
};

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token!);
  });
  failedQueue = [];
};

superadminClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config as
      | { headers?: Record<string, any>; _retry?: boolean; url?: string }
      | undefined;

    const isAuthCall =
      typeof originalRequest?.url === "string" &&
      /\/auth\/superadmin\/(request-otp|verify-otp|refresh)/.test(
        originalRequest.url
      );

    if (
      !originalRequest ||
      error?.response?.status !== 401 ||
      originalRequest._retry ||
      isAuthCall
    ) {
      return Promise.reject(error);
    }

    const refreshToken = getSuperadminRefreshToken();
    if (!refreshToken) {
      redirectToLogin();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${token}`,
            };
            resolve(superadminClient(originalRequest as any));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      const res = await axios.post<SuperadminTokens>(
        `${API_BASE_URL}/auth/superadmin/refresh`,
        { refreshToken }
      );
      const { accessToken, refreshToken: newRefresh } = res.data;
      setSuperadminTokens(accessToken, newRefresh);
      processQueue(null, accessToken);
      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        Authorization: `Bearer ${accessToken}`,
      };
      return superadminClient(originalRequest as any);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Refresh failed too — the admin has to go back through the OTP.
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

attachApiErrorHandler(superadminClient);

/* ------------------------------------------------------------------ types */

/** Keys known today. `GET /superadmin/features` is the canonical list. */
export type FeatureKey = "LOYALTY" | "CLASSES" | "STOCK" | string;

/** All keys are always present — you never receive a partial map. */
export type FeatureMap = Record<string, boolean>;

export interface SuperadminTokens {
  accessToken: string;
  refreshToken: string;
}

export interface SuperadminAdmin {
  id: string;
  phone: string;
  country?: string;
  fullName: string | null;
  lastLoginAt?: string | null;
}

export interface OrganizationUsage {
  businesses: number;
  resources: number;
  staff: number;
  clients: number;
  bookingsTotal: number;
  bookingsInWindow: number;
  /** null means the organization has never taken a booking. */
  lastBookingAt: string | null;
}

export interface OrganizationRow {
  id: string;
  name: string;
  country: string;
  timezone: string;
  createdAt: string;
  isDeleted: boolean;
  /** Gates the unauthenticated `/public/*` catalogue. Not part of `features`. */
  publicBrowseEnabled: boolean;
  features: FeatureMap;
  usage: OrganizationUsage;
}

export interface OrganizationsPage {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  /** The window `usage.bookingsInWindow` is counted over. */
  windowDays: number;
  organizations: OrganizationRow[];
}

export interface OrganizationStats {
  organization: {
    id: string;
    name: string;
    country: string;
    timezone: string;
    createdAt: string;
    isDeleted: boolean;
    billingTenantId?: string | null;
    loyaltyTenantId?: string | null;
    publicBrowseEnabled: boolean;
  };
  features: FeatureMap;
  windowDays: number;
  since: string;
  setup: {
    businesses: number;
    units: number;
    resources: number;
    products: number;
  };
  people: { staff: number; clients: number; coaches: number };
  bookings: {
    total: number;
    inWindow: number;
    upcoming: number;
    /** Keyed by status name; the set of keys varies, so iterate it. */
    byStatusInWindow: Record<string, number>;
    lastCreatedAt: string | null;
  };
  classes: { offerings: number; enrollments: number };
  packages: { offered: number; sold: number };
  stock: { movementsInWindow: number };
  notifications: { sentThisMonth: number };
}

export interface OrganizationFeatures {
  organizationId: string;
  features: FeatureMap;
}

export interface OrganizationPublicBrowse {
  organizationId: string;
  publicBrowseEnabled: boolean;
}

/* --------------------------------------------------------------- auth flow */

export interface RequestOtpResponse {
  message?: string;
  channel?: "WHATSAPP" | "SMS" | string;
  expiresInMinutes?: number;
}

/** Resend. Each call replaces the previous code. 5 requests / 5 minutes. */
export const requestSuperadminOtp = async (
  phone: string,
  country?: string
): Promise<RequestOtpResponse> => {
  const { data } = await superadminClient.post<RequestOtpResponse>(
    "/auth/superadmin/request-otp",
    country ? { phone, country } : { phone },
    // The OTP screen renders its own message (429 needs its own copy).
    { suppressToast: true } as any
  );
  return data;
};

export interface VerifyOtpResponse extends SuperadminTokens {
  message: string;
  admin: SuperadminAdmin;
}

/** Six digits, valid 10 minutes, single use. 10 requests / 5 minutes. */
export const verifySuperadminOtp = async (
  phone: string,
  code: string,
  country?: string
): Promise<VerifyOtpResponse> => {
  const { data } = await superadminClient.post<VerifyOtpResponse>(
    "/auth/superadmin/verify-otp",
    country ? { phone, code, country } : { phone, code },
    // Shown inline under the code field instead.
    { suppressToast: true } as any
  );
  setSuperadminTokens(data.accessToken, data.refreshToken);
  localStorage.setItem(SUPERADMIN_ADMIN_KEY, JSON.stringify(data.admin));
  return data;
};

export const getSuperadminMe = async (): Promise<SuperadminAdmin> => {
  const { data } = await superadminClient.get<SuperadminAdmin>(
    "/auth/superadmin/me"
  );
  localStorage.setItem(SUPERADMIN_ADMIN_KEY, JSON.stringify(data));
  return data;
};

export const getCachedSuperadmin = (): SuperadminAdmin | null => {
  try {
    const raw = localStorage.getItem(SUPERADMIN_ADMIN_KEY);
    return raw ? (JSON.parse(raw) as SuperadminAdmin) : null;
  } catch {
    return null;
  }
};

/** Invalidates every token issued to this admin, on every device. */
export const superadminLogout = async (): Promise<void> => {
  try {
    if (getSuperadminToken()) {
      await superadminClient.post("/auth/superadmin/logout", {});
    }
  } catch {
    // The session is going away either way.
  } finally {
    clearSuperadminSession();
    if (typeof window !== "undefined") window.location.replace("/login");
  }
};

/* ------------------------------------------------------------------ panel */

export const listOrganizations = async (params: {
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<OrganizationsPage> => {
  const { data } = await superadminClient.get<OrganizationsPage>(
    "/superadmin/organizations",
    {
      params: {
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 25,
        ...(params.search ? { search: params.search } : {}),
      },
    }
  );
  return data;
};

/** `days` is the rolling window, 1–365, default 30. */
export const getOrganizationStats = async (
  organizationId: string,
  days?: number
): Promise<OrganizationStats> => {
  const { data } = await superadminClient.get<OrganizationStats>(
    `/superadmin/organizations/${organizationId}/stats`,
    { params: days ? { days } : undefined }
  );
  return data;
};

/** The canonical key list and column order — read it, don't hardcode. */
export const getFeatureKeys = async (): Promise<string[]> => {
  const { data } = await superadminClient.get<{ features: string[] }>(
    "/superadmin/features"
  );
  return Array.isArray(data?.features) ? data.features : [];
};

export const getOrganizationFeatures = async (
  organizationId: string
): Promise<OrganizationFeatures> => {
  const { data } = await superadminClient.get<OrganizationFeatures>(
    `/superadmin/organizations/${organizationId}/features`
  );
  return data;
};

/**
 * Send one toggle or several. The response always carries the complete
 * resulting map, so reconcile row state from it rather than from the
 * optimistic guess.
 */
export const updateOrganizationFeatures = async (
  organizationId: string,
  features: { feature: string; enabled: boolean }[]
): Promise<OrganizationFeatures> => {
  const { data } = await superadminClient.patch<OrganizationFeatures>(
    `/superadmin/organizations/${organizationId}/features`,
    { features },
    // The caller rolls the switch back and toasts with its own copy.
    { suppressToast: true } as any
  );
  return data;
};

/**
 * A column on the organization, not a feature key. Server-side it applies at
 * once, but `/public/*` responses are cached for up to 5 minutes, so visitors
 * who already loaded the catalogue can keep reading it briefly after turning it off.
 */
export const setOrganizationPublicBrowse = async (
  organizationId: string,
  enabled: boolean
): Promise<OrganizationPublicBrowse> => {
  const { data } = await superadminClient.patch<OrganizationPublicBrowse>(
    `/superadmin/organizations/${organizationId}/public-browse`,
    { enabled },
    // The caller rolls the switch back and toasts with its own copy.
    { suppressToast: true } as any
  );
  return data;
};

/* ----------------------------------------------------------- presentation */

/** Display names live in the frontend — the API returns keys only. */
export const FEATURE_LABELS: Record<string, string> = {
  LOYALTY: "Loyalty",
  CLASSES: "Classes",
  STOCK: "Stock",
};

export const FEATURE_DESCRIPTIONS: Record<string, string> = {
  LOYALTY: "Points, rewards and loyalty tiers.",
  CLASSES: "Classes, coaches and packages.",
  STOCK: "Inventory, products and stock movements.",
};

export const featureLabel = (key: string): string =>
  FEATURE_LABELS[key] ||
  key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, " ");
